/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig, isAxiosError } from 'axios';
import { client } from './api/client.gen';
import { HttpValidationError } from './api';

// Token getter function - will be set by AuthTokenProvider
let getAccessToken: (() => Promise<string>) | null = null;

/**
 * Set the token getter function for authenticated requests.
 * Called by AuthTokenProvider after Auth0 is initialized.
 */
export const setTokenGetter = (getter: () => Promise<string>) => {
  getAccessToken = getter;
  console.log('[Auth] Token getter configured');
};

/**
 * Clear the token getter (e.g., on logout)
 */
export const clearTokenGetter = () => {
  getAccessToken = null;
};

export async function extractAxiosResponse<T>(call: Promise<(AxiosResponse<T|null, any> & {error: undefined})|(AxiosError<HttpValidationError, any> & {data: undefined, error: HttpValidationError})>) {
  return call.then(d => {
    if (isAxiosError(d)) {
      throw d.error
    }

    // This is a fix for the openapi-ts validation issue where it converts null that it gets from the backend to an empty object
    if (d.data !== null && d.data !== undefined && Object.keys(d.data).length === 0) {
      return null;
    }

    return d.data
  }).catch(err => {console.log(err); throw err;})
}


// Apply interceptors to the GLOBAL axios instance
// This ensures all axios calls (even direct imports) get the token
axios.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (getAccessToken) {
      try {
        const token = await getAccessToken();
        config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.error('[Auth] Failed to get access token:', error);
      }
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 errors
axios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      console.error('[Auth] 401 Unauthorized - token may be invalid');
    }
    return Promise.reject(error);
  }
);

// Double check the port and the version prefix!
client.setConfig({
  baseURL: import.meta.env.VITE_API_URL || "http://localhost:3001",
  axios,
});

export default axios;
